import React, { useState, useEffect, useCallback, useMemo, } from 'react';
import {
    Calendar as CalIcon, Plus, ChevronDown, ChevronLeft, ChevronRight,
    MapPin, Package, X, Settings2, Phone, CreditCard, CalendarDays, Zap
} from 'lucide-react';
import Modal from "../components/modal";
import Notification from "../components/Notification";
import dayjs from 'dayjs';
import BasicDatePicker from '../components/BasicDatePicker';
import { useSelector, useDispatch } from 'react-redux';
import { FiCalendar, FiClock, FiPhone, FiSend, FiChevronRight, FiCheckCircle, FiDollarSign, FiLink } from 'react-icons/fi';
import { getAllBranch, getPackages, getBookings, getAddons, getAvailableSlotss, updateBookingById, getBookingsList, fetchzcql, createPaymentLinkApi, updateBookingStatus, createPaymentApi } from '../api/reusableApis';
import Bookings from './Bookings';
import { Info } from 'lucide-react';

// --- Utility: Status Color Mapper ---
const getStatusStyles = (status) => {
    const s = status?.toLowerCase();

    switch (s) {
        case 'confirmed':
        case 'booked':
        case 'checked-in':
            return {
                cardBg: 'bg-emerald-50 text-emerald-800 border-emerald-200',
                badge: 'bg-emerald-100 text-emerald-900',
                dot: 'bg-emerald-500',
                isActive: true
            };

        case 'pending':
            return {
                cardBg: 'bg-amber-50 text-amber-800 border-amber-200',
                badge: 'bg-amber-100 text-amber-900',
                dot: 'bg-amber-500',
                isActive: true
            };

        case 'cancelled':
            return {
                cardBg: 'bg-rose-50 text-rose-700 border-rose-200 opacity-70',
                badge: 'bg-rose-100 text-rose-800',
                dot: 'bg-rose-500',
                isActive: false
            };

        case 'refunded':
            return {
                cardBg: 'bg-blue-50 text-blue-700 border-blue-200 opacity-70',
                badge: 'bg-blue-100 text-blue-800',
                dot: 'bg-blue-500',
                isActive: false
            };

        default:
            return {
                cardBg: 'bg-slate-50 text-slate-700 border-slate-200',
                badge: 'bg-slate-100 text-slate-800',
                dot: 'bg-slate-500',
                isActive: true
            };
    }
};

const PremiumCalendar = () => {
    const currentLoggedInUser = useSelector((state) => state.auth.currentUser);
    const [branchFilter, setBranchFilter] = useState(null);
    const [packageFilter, setPackageFilter] = useState(null);
    const [activeDayMobile, setActiveDayMobile] = useState(0);
    const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
    const [openDropdown, setOpenDropdown] = useState(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [mode, setMode] = useState("create");
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [calendarTimeSlots, setCalendarTimeSlots] = useState([]);
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [selectedTimeSlots, setSelectedTimeSlots] = useState({});
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [newTotalAmount, setNewTotalAmount] = useState(0);
    const [balanceAmount, setBalanceAmount] = useState('');
    const [extraAmtDue, setExtraAmtDue] = useState(0);

    const [status, setStatus] = useState([
        { "key": "PENDING", "value": "Pending" },
        { "key": "BOOKED", "value": "Booked" },
        { "key": "COMPLETED", "value": "Completed" },
        { "key": "REFUNDED", "value": "Refunded" },
        { "key": "CANCELED", "value": "Canceled" }
    ]);

    const [selectedAddons, setSelectedAddons] = useState([{
        addon_id: "",
        addon_name: "",
        price: 0,
        quantity: 1,
        line_total: 0
    }]);

    const closePaymentModal = () => {
        setSelectedBooking({})
        setOpenPaymentModal(false);
        setFormData({});
    }


    const handleExtraAmt = (e) => {
        setExtraAmtDue(e);
        var newTotal = Number(e) + Number(balanceAmount);
        setNewTotalAmount(newTotal);
    }

    const markComplete = async (newTotal) => {
        var booking_id = selectedBooking?.booking_id;
        let requestObj = {
            "ROWID": booking_id,
            "extra_amt": Number(extraAmtDue),
            "total_amount": Number(newTotal),
            "status": "COMPLETED"
        }

        var updateBooking = await updateBookingStatus(requestObj, booking_id);
        if (updateBooking?.data?.success === true) {
            let requestBody = {
                amount: Number(newTotal),
                payment_method: "Cash",
                notes: "Balance Payment",
                status: "completed"
            }
            var createPayment = await createPaymentApi(requestBody, booking_id);
            console.log(createPayment, 'createPayment');
            if (createPayment?.data?.success === true) {
                loadBookings();
                closePaymentModal();

                setToast({
                    message: "Payment completed successfully..",
                    type: "success",
                });
            } else if (createPayment?.data?.success === false) {
                closePaymentModal();
                setToast({
                    message: createPayment?.data?.message || 'Unable to create a payment.',
                    type: "error",
                });
            }
        } else {
            closePaymentModal();
            setToast({
                message: updateBooking?.data?.message || 'Unable to update booking record.',
                type: "error",
            });
        }
    }


    const [branches, setBranches] = useState([]);
    const [packages, setPackages] = useState([]);
    const [bookingsMap, setBookingsMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ message: "", type: "success" });
    const [deletedAddons, setDeletedAddons] = useState([]);
    const [eventDate, setEventDate] = useState(dayjs());
    const [addonTotalAmt, setAddonTotalAmt] = useState(0);
    const [addons, setAddons] = useState([]);

    const formatDate = (date) => {
        const d = new Date(date);
        return [d.getFullYear(), ('' + (d.getMonth() + 1)).padStart(2, '0'), ('' + d.getDate()).padStart(2, '0')].join('-');
    };

    // Auto-calculate Addon Totals
    useEffect(() => {
        const total = selectedAddons.reduce((sum, item) => sum + (Number(item.line_total) || 0), 0);
        setAddonTotalAmt(total);
        setFormData((prev) => {
            const baseAmount = Number(prev.package_amount || 0);
            const advancePaid = Number(prev.advance_amount || 0);
            const totalAmount = baseAmount + total;
            const balanceAmount = totalAmount - advancePaid;

            return {
                ...prev,
                addons_amount: total,
                total_amount: totalAmount,
                balance_amount: balanceAmount
            };
        });
    }, [selectedAddons]);

    const handleedit = async (booking) => {
        setSelectedBooking(booking);
        setMode('edit');
        setFormData({
            customer_name: booking?.customer_name,
            customer_email: booking?.customer_email,
            customer_phone: booking?.customer_phone,
            cake_note: booking?.cake_note,
            notes: booking?.notes,
            booking_date: booking?.booking_date || dayjs(),
            package_amount: booking?.package_amount,
            advance_amount: booking?.advance_amount,
            total_amount: booking?.total_amount,
            balance_amount: booking?.balance_amount,
            status: booking?.status,
            time_slot_id: booking?.time_slot_id
        });


        var addons = await fetchzcql(`select * from booking_addons where booking_id = '${booking.booking_id}'`);
        var addonsarray = addons.data.response;
        if (addonsarray.length > 0) {
            setSelectedAddons(
                addonsarray?.map(a => ({
                    ROWID: a.booking_addons.ROWID,
                    addon_id: a.booking_addons.addon_id,
                    addon_name: a.booking_addons.addon_name,
                    price: Number(a.booking_addons.price),
                    quantity: Number(a.booking_addons.quantity),
                    line_total: Number(a.booking_addons.line_total),
                    isExisting: true
                }))
            );
        }
        setAddonTotalAmt(booking?.addons_amount);

        var timeslots = await fetchzcql(`select * from time_slots where ROWID = '${booking?.time_slot_id}'`);
        var timeslotsobj = timeslots.data.response[0]['time_slots'];



        var alltimeslots = await fetchTimeSlots(booking?.branch_id, booking?.package_id, booking?.booking_date);
        alltimeslots = alltimeslots?.filter((t) => {
            return t.is_available;
        })
        if (booking?.status == "BOOKED") {
            var sk = timeslotsobj;
            sk.time_slot_id = timeslotsobj.ROWID;
            alltimeslots.push(sk);
            setAvailableTimeSlots(alltimeslots);
        }

        setSelectedTimeSlots(timeslotsobj);
        setOpenModal(true);
    };
    const sendPaymentLink = async (status) => {
        if (status === 'BOOKED') {
            // setLoader(true);
            var order_id = `ORDER_${formData['booking_id']}`;

            let requestObj = {
                "orderId": order_id,
                "amount": Number(newTotalAmount),
                "customerName": formData['customer_name'],
                "email": formData['customer_email'],
                "mobile": formData['customer_phone'],
                "booking_id": formData['booking_id'],
                "status": status,
                "notes": {
                    "booking_id": formData['booking_id']
                }
            }

            var paymentDetails = await createPaymentLinkApi(requestObj);
            console.log(paymentDetails, 'payment details');
            // setLoader(false);
            setOpenPaymentModal(false);
            if (paymentDetails?.data?.success === true) {
                setToast({
                    message: "Payment Link Sent to Customer",
                    type: "success",
                });
            } else if (paymentDetails?.data?.success === false) {
                setToast({
                    message: paymentDetails?.data?.message || 'Unable to create a payment link.',
                    type: "error",
                });
            } else {
                setToast({
                    message: "Something went wrong, Contact Administrator.",
                    type: "error",
                });
            }
        }
    }

    const handlePayment = (booking) => {
        setSelectedBooking(booking);
        if (booking?.['status'] === 'BOOKED') {
            let formObj = {
                customer_name: booking?.['customer_name'],
                customer_email: booking?.['customer_email'],
                customer_phone: booking?.['customer_phone'],
                cake_note: booking?.['cake_note'],
                notes: booking?.['notes'],
                booking_date: booking?.booking_date || dayjs(),
                package_amount: booking?.['package_amount'],
                advance_amount: booking?.['advance_amount'],
                total_amount: booking?.['total_amount'],
                balance_amount: booking?.['balance_amount'],
                status: booking?.['status'],
                time_slot_id: booking?.['time_slot_id'],
                booking_id: booking?.['booking_id']
            }
            setFormData(formObj);
            setOpenPaymentModal(true);
            setExtraAmtDue(0);
            setBalanceAmount(booking?.['balance_amount']);
            setNewTotalAmount(booking?.['balance_amount']);
        }
    }

    const fetchTimeSlots = useCallback(async (branch_id, package_id, booking_date) => {
        try {
            const timeSlots = await getAvailableSlotss(branch_id, package_id, booking_date);
            setAvailableTimeSlots(timeSlots?.data?.slots || []);
            return timeSlots?.data?.slots;
        } catch (error) {
            console.error(error);
        }
    }, []);

    const closeModal = () => setOpenModal(false);

    const getDaysInWeek = (startDate) => {
        const start = new Date(startDate);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek;
        const sunday = new Date(new Date(start).setDate(diff));

        return Array.from({ length: 7 }, (_, i) => {
            const day = new Date(sunday);
            day.setDate(sunday.getDate() + i);
            return day;
        });
    };

    const weekDays = useMemo(() => getDaysInWeek(currentWeekStart), [currentWeekStart]);

    const weekRangeLabel = useMemo(() => {
        const start = weekDays[0];
        const end = weekDays[6];
        const options = { month: 'short', day: 'numeric' };
        return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
    }, [weekDays]);

    useEffect(() => {
        const todayStr = new Date().toDateString();
        const todayIdx = weekDays.findIndex(d => d.toDateString() === todayStr);
        if (todayIdx !== -1) setActiveDayMobile(todayIdx);
    }, [weekDays]);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                let branchData = await getAllBranch(true);
                if (branchData?.length > 0) {
                    if (currentLoggedInUser.role_details.role_name === "App User") {
                        branchData = branchData.filter(b => b.ROWID === currentLoggedInUser.users.branch_id);
                    }
                    setBranches(branchData);
                    setBranchFilter(branchData[0]);
                }
                const addonData = await getAddons();
                setAddons(addonData || []);
            } catch (err) { console.error(err); }
        };
        loadInitialData();
    }, []);

    useEffect(() => {
        if (!branchFilter?.Branch_ID) return;
        const loadPackages = async () => {
            try {
                const packageData = await getPackages(branchFilter.Branch_ID, true);
                if (packageData?.length > 0) {
                    setPackages(packageData);
                    setPackageFilter(packageData[0]);
                }
            } catch (err) { console.error(err); }
        };
        loadPackages();
    }, [branchFilter]);

    const loadBookings = async () => {
        setLoading(true);
        try {
            const response = await getBookings(
                branchFilter.Branch_ID,
                formatDate(weekDays[0]),
                formatDate(weekDays[6]),
                packageFilter.ROWID
            );
            if (response?.[0]?.slots_by_date) {
                setBookingsMap(response[0].slots_by_date);
                const allLabels = new Set();
                Object.values(response[0].slots_by_date).forEach(daySlots => {
                    daySlots.forEach(slot => { if (slot?.label) allLabels.add(slot.label); });
                });

                const parseStartTime = (label) => {
                    const start = label.split(' - ')[0];
                    const [time, period] = start.split(' ');
                    let [hours, minutes] = time.split(':').map(Number);
                    if (period === 'PM' && hours !== 12) hours += 12;
                    if (period === 'AM' && hours === 12) hours = 0;
                    return hours * 60 + minutes;
                };

                const sortedSlots = Array.from(allLabels).sort((a, b) => parseStartTime(a) - parseStartTime(b));
                setCalendarTimeSlots(sortedSlots);
            } else {
                setBookingsMap({});
                setCalendarTimeSlots([]);
            }
        } catch (err) { setBookingsMap({}); } finally { setLoading(false); }
    };

    useEffect(() => {
        if (branchFilter?.Branch_ID && packageFilter?.ROWID) loadBookings();
    }, [branchFilter, packageFilter, weekDays]);

    const removeAddons = (addon) => {
        if (addon?.isExisting) setDeletedAddons((prev) => [...prev, addon.ROWID]);
    };

    const updateBookings = async () => {
        const booking_id = selectedBooking?.['booking_id'];

        // Filter for only brand new addons
        const newAddons = selectedAddons
            ?.filter(addon => !addon.isExisting)
            .map(addon => ({
                booking_id: booking_id,
                addon_id: addon.addon_id,
                addon_name: addon.addon_name,
                price: addon.price,
                quantity: addon.quantity,
                line_total: addon.line_total,
            }));
        const requestBody = {
            json: { ...formData, ROWID: booking_id },
            deletedAddons: deletedAddons,
            addons: newAddons
        };

        try {
            const response = await updateBookingById(requestBody, booking_id);
            if (response?.data?.success) {
                setToast({ message: 'Booking updated successfully.', type: "success" });
                loadBookings();
                closeModal();
            } else {
                setToast({ message: response?.data?.message || 'Update failed.', type: "error" });
            }
        } catch (error) {
            setToast({ message: 'A network error occurred.', type: "error" });
        }
    };

    const handleEventDate = (value) => {
        setEventDate(value);
        setFormData(prev => ({ ...prev, booking_date: value }));
    };

    const toggleDropdown = (name) => setOpenDropdown(openDropdown === name ? null : name);

    return (
        <>
            <Notification
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ message: "", type: "success" })} />

            <div className="h-screen w-full bg-[#F1F5F9] p-3 md:p-8 flex flex-col font-sans overflow-hidden text-slate-900">
                {isDatePickerOpen && <CustomDatePicker onClose={() => setIsDatePickerOpen(false)} onSelect={(date) => { setCurrentWeekStart(date); setIsDatePickerOpen(false); }} selectedDate={currentWeekStart} />}

                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 gap-6 pt-16 md:pt-0">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <div className={`h-2.5 w-2.5 rounded-full ${loading ? 'bg-purple-500 animate-ping' : 'bg-purple-500'}`}></div>
                            <span className="text-[12px] font-bold text-slate-500 uppercase tracking-widest">Live Schedule</span>
                        </div>
                        <h1 className="text-3xl font-black text-slate-900 tracking-tight">Event Management</h1>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-row items-center gap-3 w-full xl:w-auto">
                        <FilterDropdown icon={<MapPin size={16} />} label={branchFilter?.Branch_Name || "Branch"} options={branches} isOpen={openDropdown === 'branch'} toggle={() => toggleDropdown('branch')} onSelect={setBranchFilter} />
                        <FilterDropdown icon={<Package size={16} />} label={packageFilter?.name || "Package"} options={packages} isOpen={openDropdown === 'package'} toggle={() => toggleDropdown('package')} onSelect={setPackageFilter} />
                        <div className="flex flex-row items-center gap-2 w-full md:w-auto bg-white border border-slate-300 rounded-full p-1 shadow-sm h-12">
                            <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() - 7); setCurrentWeekStart(d); }} className="p-2 hover:bg-slate-100 rounded-full text-slate-600"><ChevronLeft size={24} /></button>
                            <DateFilterDropdown weekRangeLabel={weekRangeLabel} isOpen={openDropdown === 'date'} toggle={() => toggleDropdown('date')} onSelect={(date) => { setCurrentWeekStart(date); setOpenDropdown(null); }} openCustomPicker={() => { setOpenDropdown(null); setIsDatePickerOpen(true); }} />
                            <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() + 7); setCurrentWeekStart(d); }} className="p-2 hover:bg-slate-100 rounded-full text-slate-600"><ChevronRight size={24} /></button>
                        </div>
                    </div>
                </div>

                {/* Mobile Date Selector */}
                <div className="flex md:hidden overflow-x-auto no-scrollbar gap-2 mb-4 pb-2">
                    {weekDays.map((date, idx) => (
                        <button
                            key={idx}
                            onClick={() => setActiveDayMobile(idx)}
                            className={`flex-shrink-0 w-14 h-16 rounded-2xl flex flex-col items-center justify-center transition-all border-2 
                            ${activeDayMobile === idx ? 'bg-purple-600 border-purple-600 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-600'}`}
                        >
                            <span className="text-[10px] font-bold uppercase">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                            <span className="text-lg font-black">{date.getDate()}</span>
                        </button>
                    ))}
                </div>

                <div className={`flex-1 bg-white rounded-2xl md:rounded-3xl shadow-xl border border-slate-300 flex flex-col overflow-hidden ${loading ? 'opacity-50' : 'opacity-100'}`}>
                    <div className="hidden md:grid grid-cols-8 bg-slate-100/80 border-b border-slate-300">
                        <div className="p-5 border-r border-slate-300 flex items-center justify-center"><Settings2 size={18} className="text-slate-500" /></div>
                        {weekDays.map((date, idx) => (
                            <div key={idx} className="p-4 text-center border-r border-slate-300 last:border-0">
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-0.5">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                                <span className={`text-sm font-black ${date.toDateString() === new Date().toDateString() ? 'text-purple-600 underline underline-offset-4' : 'text-slate-700'}`}>{date.getDate()}</span>
                            </div>
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        {calendarTimeSlots.map((slotLabel) => (
                            <div key={slotLabel} className="grid grid-cols-1 md:grid-cols-8 border-b border-slate-300 last:border-0 min-h-[160px]">
                                <div className="p-4 border-r border-slate-300 bg-slate-50 flex items-center md:items-start justify-start md:justify-center">
                                    <span className="text-xs font-black text-slate-700 bg-white border border-slate-300 px-4 py-2 rounded-full shadow-sm">{slotLabel}</span>
                                </div>

                                {weekDays.map((date, idx) => {
                                    const dateKey = formatDate(date);
                                    debugger
                                    const dayData = bookingsMap[dateKey] || [];
                                    const slotInfo = dayData.find(s => s.label === slotLabel);
                                    const allBookings = slotInfo?.all_bookings || [];
                                    const hasActiveBooking = allBookings.some(b => getStatusStyles(b.status).isActive);
                                    const showAvailable = allBookings.length === 0 || !hasActiveBooking;
                                    const BookedBy = slotInfo?.booked_by || "";

                                    return (
                                        <div key={idx} className={`p-3 border-r border-slate-300 last:border-0 bg-white ${activeDayMobile === idx ? 'block' : 'hidden md:block'}`}>
                                            <div className="flex flex-col gap-3 h-full">
                                                {allBookings.map((booking, bIdx) => {
                                                    const statusStyle = getStatusStyles(booking.status);
                                                    return (
                                                        <div key={bIdx} className={`group relative rounded-2xl p-4 border-2 ${statusStyle.cardBg} transition-all`}>
                                                            <div className="flex justify-between items-start mb-2">
                                                                <span className={`px-2.5 py-1 rounded text-[10px] font-black uppercase shadow-sm ${statusStyle.badge}`}>
                                                                    {booking.status}
                                                                </span>

                                                                {/* Info Icon with Tooltip - scoped to this div only */}
                                                                {/* Info Icon with Bottom Tooltip */}
                                                                {BookedBy && (
                                                                    <div className="relative group/info">
                                                                        {/* Using cursor-pointer for the standard hand icon */}
                                                                        <div className="cursor-pointer text-slate-400 hover:text-slate-600 transition-colors">
                                                                            <Info size={16} />
                                                                        </div>

                                                                        {/* Tooltip: Positioned at top-full to appear BELOW the icon */}
                                                                        <div className="absolute top-full right-0 mt-2 hidden group-hover/info:block z-50 pointer-events-none transition-all">
                                                                            <div className="bg-slate-900 text-white text-[10px] py-1.5 px-3 rounded-lg shadow-xl whitespace-nowrap">
                                                                                {/* Pointer Arrow (Flipped to point UP) */}
                                                                                <div className="absolute bottom-[99%] right-1.5 border-[5px] border-transparent border-b-slate-900"></div>

                                                                                <span className="opacity-70 font-medium tracking-tight">Booked by:</span>
                                                                                <span className="ml-1.5 font-black uppercase text-[9px]">{BookedBy}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <h4 className="text-sm font-black truncate mb-1">{booking.customer_name}</h4>
                                                            <div className="text-xs opacity-80 flex items-center gap-1.5 font-medium">
                                                                <Phone size={12} /> {booking.customer_phone}
                                                            </div>

                                                            {/* Action Buttons */}
                                                            <div className="mt-4 flex flex-col gap-2">
                                                                <button
                                                                    onClick={() => handleedit(booking)}
                                                                    className="w-full py-2.5 bg-white/70 border border-black/5 rounded-xl text-xs font-bold text-slate-800 hover:bg-white transition-all"
                                                                >
                                                                    Manage
                                                                </button>
                                                                {booking.status === 'BOOKED' && (
                                                                    <button
                                                                        onClick={() => { handlePayment(booking) }}
                                                                        className="w-full py-2.5 bg-[#72398E] text-[9px] border rounded-xl font-bold text-white transition-all shadow-sm flex items-center justify-center gap-1.5"
                                                                    >
                                                                        <CreditCard size={14} />
                                                                        Pay Link
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {showAvailable && (
                                                    <button className="flex-1 w-full rounded-2xl border-2 border-slate-100 bg-white flex flex-col items-center justify-center group hover:bg-slate-50 hover:border-slate-300 transition-all min-h-[120px] gap-2" onClick={() => { setMode("create"); setOpenModal(true); }}>
                                                        <div className="bg-slate-50 p-2 rounded-full border border-slate-200 text-slate-400 group-hover:text-purple-500 group-hover:border-purple-200 transition-all"><Plus size={20} /></div>
                                                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest group-hover:text-slate-600">Available</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
                <style jsx>{`
                    .custom-scrollbar::-webkit-scrollbar { width: 8px; }
                    .custom-scrollbar::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 10px; border: 2px solid #F1F5F9; }
                    .no-scrollbar::-webkit-scrollbar { display: none; }
                    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                `}</style>
            </div>
            <Modal
                open={openModal}
                onClose={closeModal}
                title={
                    <span className="flex items-center gap-2">
                        {mode === "view" ? "👁️ View Booking" : mode === "edit" ? "✏️ Edit Booking" : ""}
                    </span>
                }
                width="w-full sm:w-[95%] md:max-w-3xl"
            >
                {mode === 'create' ? (
                    <Bookings />
                ) : (
                    /* Main Container: Uses flex-col and a fixed max height */
                    <div className="flex flex-col max-h-[72vh]">

                        {/* 1. Scrollable Body: flex-1 ensures it grows to fill space, overflow-y-auto handles the scroll */}
                        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-10">

                            {/* Customer Details Section */}
                            <section>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="h-4 w-1 bg-[#72398E] rounded-full"></div>
                                    <h3 className="text-[11px] uppercase tracking-widest font-black text-slate-400">
                                        Customer Details
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <FormInput
                                        label="Customer Name"
                                        value={formData.customer_name}
                                        onChange={(val) => {
                                            setFormData((p) => ({ ...p, customer_name: val }));
                                            setErrors((prev) => ({ ...prev, customer_name: null }));
                                        }}
                                        placeholder="Full Name"
                                        disabled={mode === "view"}
                                        error={errors.customer_name}
                                    />
                                    <FormInput
                                        label="Customer Email"
                                        value={formData.customer_email}
                                        onChange={(val) => {
                                            setFormData((p) => ({ ...p, customer_email: val }));
                                            setErrors((prev) => ({ ...prev, customer_email: null }));
                                        }}
                                        placeholder="Email Address"
                                        disabled={mode === "view"}
                                        error={errors.customer_email}
                                    />
                                    <div className="sm:col-span-2">
                                        <FormInput
                                            label="Customer Phone"
                                            value={formData.customer_phone}
                                            onChange={(val) => {
                                                setFormData((p) => ({ ...p, customer_phone: val }));
                                                setErrors((prev) => ({ ...prev, customer_phone: null }));
                                            }}
                                            placeholder="Phone Number"
                                            disabled={mode === "view"}
                                            error={errors.customer_phone}
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* General Details Section */}
                            <section className="rounded-2xl">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="h-4 w-1 bg-[#72398E] rounded-full"></div>
                                    <h3 className="text-[11px] uppercase tracking-widest font-black text-slate-400">
                                        General Details
                                    </h3>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <FormInput
                                        label="Cake Note"
                                        value={formData.cake_note}
                                        onChange={(val) => setFormData((p) => ({ ...p, cake_note: val }))}
                                        placeholder="Message on cake"
                                        disabled={mode === "view"}
                                    />
                                    <FormInput
                                        label="Internal Notes"
                                        value={formData.notes}
                                        onChange={(val) => setFormData((p) => ({ ...p, notes: val }))}
                                        placeholder="Special instructions"
                                        disabled={mode === "view"}
                                    />
                                </div>

                                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-5">
                                    <div>
                                        <label className="block mb-1.5 text-xs font-bold text-slate-600 uppercase">Date</label>
                                        <BasicDatePicker handleDatePicker={handleEventDate} valueData={formData.booking_date} disable={true} />
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label className="block mb-1.5 text-xs font-bold text-slate-600 uppercase tracking-wider">Status</label>
                                        <select
                                            value={formData.status || ''}
                                            onChange={(e) => setFormData(p => ({ ...p, status: e.target.value }))}
                                            className="w-full h-11 px-4 rounded-xl border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-[#72398E] disabled:bg-slate-100 transition-all appearance-none"
                                            disabled={mode === "view"}
                                        >
                                            <option value="">Select status</option>
                                            {status.map(b => <option key={b.key} value={b.key}>{b.value}</option>)}
                                        </select>
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label className="block mb-1.5 text-xs font-bold text-slate-600 uppercase tracking-wider">Time-slot</label>
                                        <select
                                            value={formData.time_slot_id || ''}
                                            onChange={(e) => setFormData(p => ({ ...p, time_slot_id: e.target.value }))}
                                            className="w-full h-11 px-4 rounded-xl border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-[#72398E] disabled:bg-slate-100 transition-all appearance-none"
                                            disabled={mode === "view"}
                                        >
                                            <option value="">Select timeslot</option>
                                            {availableTimeSlots.map(b => <option key={b.time_slot_id} value={b.time_slot_id}>{b.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </section>

                            {/* Addons Section */}
                            <section>
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-3">
                                        <div className="h-4 w-1 bg-[#72398E] rounded-full"></div>
                                        <h3 className="text-[11px] uppercase tracking-widest font-black text-slate-400">
                                            Addons
                                        </h3>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {selectedAddons && selectedAddons.length > 0 ? (
                                        <>
                                            {selectedAddons.map((addon, index) => (
                                                <div key={index} className="relative group bg-white border border-slate-200 p-4 rounded-2xl transition-all hover:border-[#72398E]/30 hover:shadow-md">
                                                    <div className="grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-12 gap-4 items-end">
                                                        <div className="sm:col-span-2 lg:col-span-5">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block ml-1">Add-on Item</label>
                                                            <select
                                                                value={addon.addon_id || ''}
                                                                onChange={(e) => {
                                                                    const addon_id = e.target.value;
                                                                    const selected = addons.find(a => a.ROWID === addon_id);
                                                                    const branches = selected?.branches ?? [];
                                                                    const price = branches.length > 0 ? (branches.find(b => b.Branch_ID === branchFilter.Branch_ID)?.price ?? branches[0].price) : '';
                                                                    setSelectedAddons(prev => prev.map((item, i) => i === index ? {
                                                                        ...item,
                                                                        addon_id,
                                                                        addon_name: selected?.name || "",
                                                                        price: price || 0,
                                                                        line_total: (item.quantity || 1) * (price || 0)
                                                                    } : item));
                                                                }}
                                                                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-[#72398E] appearance-none"
                                                                disabled={addon.isExisting || mode === "view"}
                                                            >
                                                                <option value="">Select Addon</option>
                                                                {addons.map(b => <option key={b.ROWID} value={b.ROWID}>{b.name}</option>)}
                                                            </select>
                                                        </div>

                                                        <div className="lg:col-span-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block ml-1">Qty</label>
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                value={addon.quantity || ''}
                                                                onChange={(e) => {
                                                                    const quantity = Number(e.target.value || 0);
                                                                    setSelectedAddons(prev => prev.map((item, i) => i === index ? {
                                                                        ...item, quantity, line_total: quantity * (Number(item.price) || 0)
                                                                    } : item));
                                                                }}
                                                                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm"
                                                                disabled={addon.isExisting || mode === "view"}
                                                            />
                                                        </div>

                                                        <div className="lg:col-span-3">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block ml-1">Subtotal</label>
                                                            <div className="h-10 px-3 flex items-center bg-slate-50 border border-slate-100 rounded-lg text-sm font-semibold text-slate-600">
                                                                ₹{addon.line_total || 0}
                                                            </div>
                                                        </div>

                                                        {selectedAddons.length > 1 && mode !== "view" && (
                                                            <div className="lg:col-span-2 flex justify-end">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setSelectedAddons(prev => prev.filter((_, i) => i !== index));
                                                                        removeAddons(addon);
                                                                    }}
                                                                    className="h-10 w-10 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                                                                >
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}

                                            {mode !== "view" && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedAddons(prev => [...prev, { addon_id: "", addon_name: "", price: 0, quantity: 1, line_total: 0, isExisting: false }])}
                                                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-200 rounded-2xl text-slate-500 hover:text-[#72398E] hover:border-[#72398E] hover:bg-purple-50 transition-all font-medium text-sm"
                                                >
                                                    + Add Another Addon
                                                </button>
                                            )}

                                            <div className="flex items-center justify-between p-4 bg-[#72398E]/5 border border-[#72398E]/10 rounded-2xl">
                                                <span className="text-sm font-bold text-slate-600">Total Addons Cost</span>
                                                <span className="text-lg font-black text-[#72398E]">₹{addonTotalAmt}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-slate-200 text-slate-400 text-sm">
                                            No addons selected
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Billing Details Section */}
                            <section>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="h-4 w-1 bg-[#72398E] rounded-full"></div>
                                    <h3 className="text-[11px] uppercase tracking-widest font-black text-slate-400">
                                        Billing Summary
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <FormInput label="Package" value={formData.package_amount} disabled />
                                    <FormInput label="Advance" value={formData.advance_amount} disabled />
                                    <FormInput label="Total" value={formData.total_amount} disabled />
                                    <FormInput label="Balance" value={formData.balance_amount} disabled />
                                </div>
                            </section>
                        </div>

                        {/* 2. Fixed Footer: Placed outside the scrollable div */}
                        <div className="p-4 sm:p-6 bg-white border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-3 justify-end shrink-0">
                            <button
                                onClick={closeModal}
                                className="px-6 py-3 rounded-xl text-sm font-bold text-slate-400 hover:bg-slate-100 transition-colors"
                            >
                                Discard Changes
                            </button>

                            {mode !== "view" && (
                                <button
                                    onClick={updateBookings}
                                    className="px-10 py-3 rounded-xl bg-[#72398E] text-white text-sm font-bold shadow-lg shadow-purple-200 hover:bg-[#5a2d71] transition-all active:scale-95"
                                >
                                    {mode === "edit" ? "Update Booking" : "Save Booking"}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={openPaymentModal}
                onClose={closePaymentModal}
                title={formData.status === 'PENDING' ? "Request Advance" : "Final Settlement"}
                width="w-full sm:w-[95%] md:max-w-xl"
            >
                <div className="mx-auto bg-white">
                    {formData.status === 'BOOKED' ? (
                        <div className="space-y-6">
                            {/* Summary Card */}
                            <div className="p-5 rounded-2xl bg-[#72398E]/5 border border-purple-100 flex justify-between items-center">
                                <div>
                                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Paid Advance</span>
                                    <span className="text-xl font-black text-slate-900">₹{formData.advance_amount}</span>
                                </div>
                                <div className="text-right">
                                    <span className="block text-[10px] font-black text-[#72398E] uppercase tracking-widest mb-1">Total Due</span>
                                    <span className="text-2xl font-black text-[#72398E]">₹{newTotalAmount}</span>
                                </div>
                            </div>

                            {/* Input Section */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Original Balance</label>
                                    <div className="px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-500 font-bold text-sm">
                                        ₹{balanceAmount}
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-[#72398E] uppercase ml-1">Add Extra Charges</label>
                                    <input
                                        type="number"
                                        value={extraAmtDue || ""} // Shows empty box instead of 0 if preferred
                                        onChange={(e) => handleExtraAmt(e.target.value)}// Passes the event 'e' automatically
                                        placeholder="0.00"
                                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-100 focus:border-[#72398E] focus:outline-none font-bold text-slate-900"
                                    />
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="space-y-3 pt-2">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={() => markComplete(newTotalAmount)}
                                        className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-slate-900 text-white text-sm font-black transition-all hover:bg-black active:scale-95"
                                    >
                                        <FiDollarSign /> Mark as Paid (Cash)
                                    </button>
                                    <button
                                        onClick={() => sendPaymentLink('BOOKED')}
                                        className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-[#72398E] text-white text-sm font-black shadow-lg shadow-purple-100 hover:bg-[#5e2e75] active:scale-95"
                                    >
                                        <FiLink /> Send Link
                                    </button>
                                </div>
                                <button
                                    className="w-full py-2 text-[11px] font-black text-slate-300 uppercase tracking-widest hover:text-slate-500 transition-colors"
                                    onClick={closePaymentModal}>
                                    Close Window
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </Modal>
        </>
    );
};

// --- Sub-components (Helpers) ---
const DateFilterDropdown = ({ weekRangeLabel, isOpen, toggle, onSelect, openCustomPicker }) => {
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);
    return (
        <div className="relative h-full flex items-center justify-center md:justify-start flex-1 md:flex-none">
            <button onClick={toggle} className="px-6 border-x border-slate-300 h-full flex flex-col items-center justify-center min-w-[140px] hover:bg-slate-50 group">
                <span className="text-[9px] font-black text-purple-500 uppercase tracking-widest leading-none mb-0.5">Filter Week</span>
                <div className="flex items-center justify-center gap-1 w-full">
                    <span className="text-xs font-black text-slate-700 uppercase group-hover:text-purple-600">{weekRangeLabel}</span>
                    <ChevronDown size={12} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>
            {isOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white border border-slate-300 rounded-2xl shadow-2xl z-[100] py-2">
                    <button onClick={() => onSelect(today)} className="w-full text-left px-5 py-3 text-xs font-bold text-slate-700 hover:bg-purple-50 flex items-center gap-3"><Zap size={14} className="text-purple-500" /> Today</button>
                    <button onClick={() => onSelect(nextWeek)} className="w-full text-left px-5 py-3 text-xs font-bold text-slate-700 hover:bg-purple-50 flex items-center gap-3"><CalendarDays size={14} className="text-purple-500" /> Next Week</button>
                    <button onClick={openCustomPicker} className="w-full text-left px-5 py-3 text-xs font-bold text-slate-700 hover:bg-purple-50 flex items-center gap-3"><CalIcon size={14} className="text-slate-400" /> Custom...</button>
                </div>
            )}
        </div>
    );
};

const FilterDropdown = ({ icon, label, options, isOpen, toggle, onSelect }) => (
    <div className="relative flex-1 sm:flex-none">
        <button onClick={toggle} className="w-full flex items-center justify-between gap-3 px-5 py-3 bg-white border border-slate-300 rounded-2xl text-sm font-bold text-slate-700 shadow-sm truncate">
            <div className="flex items-center gap-2 truncate"><span className="text-purple-500">{icon}</span><span className="truncate">{label}</span></div>
            <ChevronDown size={16} className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
            <div className="absolute left-0 mt-2 w-full sm:w-64 bg-white border border-slate-300 rounded-2xl shadow-2xl z-[70] py-2 max-h-72 overflow-y-auto">
                {options.map((opt, index) => (
                    <button key={index} onClick={() => { onSelect(opt); toggle(); }} className="w-full text-left px-5 py-3 text-sm font-semibold text-slate-600 hover:bg-purple-50 border-b border-slate-50 last:border-0">{opt.Branch_Name || opt.name}</button>
                ))}
            </div>
        )}
    </div>
);

const CustomDatePicker = ({ onClose, onSelect, selectedDate }) => {
    const [viewDate, setViewDate] = useState(new Date(selectedDate));
    const monthName = viewDate.toLocaleString('default', { month: 'long' });
    const year = viewDate.getFullYear();
    const daysInMonth = new Date(year, viewDate.getMonth() + 1, 0).getDate();
    const firstDay = (new Date(year, viewDate.getMonth(), 1).getDay() + 6) % 7;
    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[360px] overflow-hidden border border-slate-300">
                <div className="bg-purple-600 p-8 text-white text-center">
                    <h3 className="text-xl font-black uppercase mb-6">{monthName} {year}</h3>
                    <div className="flex justify-between items-center bg-white/10 rounded-2xl p-2">
                        <button onClick={() => setViewDate(new Date(year, viewDate.getMonth() - 1, 1))} className="p-3 hover:bg-white/10 rounded-xl"><ChevronLeft size={22} /></button>
                        <button onClick={onClose} className="text-xs font-black uppercase px-4 py-2">Close</button>
                        <button onClick={() => setViewDate(new Date(year, viewDate.getMonth() + 1, 1))} className="p-3 hover:bg-white/10 rounded-xl"><ChevronRight size={22} /></button>
                    </div>
                </div>
                <div className="p-6 grid grid-cols-7 gap-1 text-center">
                    {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map(d => <span key={d} className="text-xs font-black text-slate-400 py-3">{d}</span>)}
                    {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                        const d = new Date(year, viewDate.getMonth(), i + 1);
                        const isSelected = d.toDateString() === selectedDate.toDateString();
                        return (
                            <button key={i} onClick={() => onSelect(d)} className={`h-11 text-sm font-bold rounded-xl ${isSelected ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-purple-50'}`}>{i + 1}</button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

function FormInput({ label, value, onChange, placeholder, readOnly, disabled, type = "text", error }) {
    const isInactive = readOnly || disabled;

    return (
        <div className="flex flex-col group transition-all duration-300">
            {/* Label - Reduced size, increased letter spacing for a 'pro' feel */}
            <label className={`
                text-[10px] font-black uppercase tracking-[0.15em] mb-1.5 ml-1 transition-colors duration-300
                ${error ? 'text-rose-500' : 'text-slate-400 group-focus-within:text-[#72398E]'}
            `}>
                {label}
            </label>

            <div className="relative">
                <input
                    type={type}
                    placeholder={placeholder}
                    value={value || ""}
                    readOnly={readOnly}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    className={`
                        w-full px-4 py-3 text-sm font-medium transition-all duration-300 rounded-2xl outline-none border
                        /* Interaction States */
                        ${isInactive
                            ? 'bg-slate-50/50 border-slate-100 text-slate-400 cursor-not-allowed shadow-inner'
                            : error
                                ? 'border-rose-200 bg-rose-50/30 text-rose-900 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10'
                                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 focus:border-[#72398E] focus:ring-4 focus:ring-purple-500/5 focus:shadow-sm focus:-translate-y-[1px]'
                        }
                    `}
                />

                {/* Optional: Error Icon for quick scanning */}
                {error && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-500">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                    </div>
                )}
            </div>

            {/* Error Message with Slide-down Animation */}
            <div className={`overflow-hidden transition-all duration-300 ${error ? 'max-h-10 opacity-100' : 'max-h-0 opacity-0'}`}>
                <p className="text-[11px] text-rose-500 mt-1.5 ml-1 font-semibold flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-rose-500" />
                    {error}
                </p>
            </div>
        </div>
    );
}

export default PremiumCalendar;